import cookies from 'js-cookie';
import { createAccount, profileApi, getErrorMessages, removeErrors, getSelectedCustomerAccountId } from './api';
import { select } from '../shared/utils/dom';
import { delimiters } from '../shared/constants';
import loadingMixin from '../shared/vue/mixins/loading';
import togglePassword from '../shared/vue/mixins/togglePassword';
import scrollUp from '../shared/vue/mixins/scrollUp';
import { createError, removeError } from '../shared/formErrors';
import { regex, isPasswordValid } from '../shared/constants/validation';

/*
* Enroll in paperless billing
*/
const editProfileEmailValidationCountCookieName = 'editProfileEmailValidationCount';
const el = select('[data-vue-app="edit-profile"]');
if (el) {
  const app = new Vue({ // eslint-disable-line no-unused-vars
    el,
    mixins: [loadingMixin, scrollUp, togglePassword],
    delimiters,
    data: {
      error: false,
      // for accessibility in live regions
      liveText: '',
      success: false,
      passwordResetSuccess: false,
      editFirstName: '',
      editLastName: '',
      editBusinessName: '',
      editEmail: '',
      emailChanged: false,
      originalEmail: '',
      confirmEmail: '',
      editPassword: null,
      confirmPassword: null,
      ldc: null,
      // password valid
      modelPasswordValid: false,
      // confirm password matches
      modelConfirmPasswordMatch: false,
      verifyPassword: null,
      errorMessages: getErrorMessages(),
      emailValidationFailed: false,
      emailValidationFailedCount: 0,
    },

    created() {
      Event.$on('A11yDialogConfirm:verify-password', () => {
        this.verifyPassword = select('#current-password').value;
        this.validateAndSubmit();
      });
      Event.$on('A11yDialogConfirm:verify-emailChanged', () => {
        this.verifyPassword = select('#current-password1').value;
        this.validateAndSubmit();
      });
    },

    mounted() {
      if (this.$el.dataset.initFirstName) {
        this.editFirstName = this.$el.dataset.initFirstName;
      }

      if (this.$el.dataset.initLastName) {
        this.editLastName = this.$el.dataset.initLastName;
      }

      if (this.$el.dataset.initBusinessName) {
        this.editBusinessName = this.$el.dataset.initBusinessName;
      }

      if (this.$el.dataset.initEmail) {
        this.editEmail = this.$el.dataset.initEmail;
        this.confirmEmail = this.editEmail;
        this.originalEmail = this.editEmail;
      }

      if (this.$el.dataset.ldc) {
        this.ldc = this.$el.dataset.ldc;
      }
      this.emailValidationFailedCount = cookies.get(editProfileEmailValidationCountCookieName) ? JSON.parse(cookies.get(editProfileEmailValidationCountCookieName)) : 0;
    },

    computed: {
      emailValidationFailedThresholdLimitReached() {
        return this.emailValidationFailedCount >= 3;
      },
    },

    watch: {
      editEmail(newValue, oldValue) {
        this.emailChanged = newValue !== this.originalEmail;

        if (!this.emailChanged) {
          this.originalEmail = oldValue;
        }
      },
    },

    /**
     * Methods
     */
    methods: {
      /**
       * Keyup on password field
       * validates the password and matching password on keyup
       */
      keyUpPassword(event) {
        // password validity check
        if (event.target === this.$refs.editPassword) {
          if (this.editPassword && this.editPassword.length < 8) {
            this.modelPasswordValid = false;
          } else if (!isPasswordValid(this.$refs.editPassword.value)) {
            this.modelPasswordValid = false;
          } else {
            this.modelPasswordValid = true;
          }
        }

        // confirm password validity check
        if (event.target === this.$refs.confirmPassword) {
          // check they are the same thing
          if (this.confirmPassword && this.editPassword && this.modelPasswordValid && this.confirmPassword === this.editPassword) {
            this.modelConfirmPasswordMatch = true
            removeError(this.$refs.editPassword, this.$refs.editPasswordError);
          } else {
            this.modelConfirmPasswordMatch = false
            removeError(this.$refs.confirmPassword, this.$refs.confirmPasswordError);
          }
        }
      },

      saveProfileInformation() {
        if (this.emailChanged || this.editPassword || this.confirmPassword) {
          if (!this.validate()) {
            if (this.emailChanged) {
              Event.$emit('A11yDialogOpen', { dialogKey: 'verify-emailChanged' });
            } else {
              Event.$emit('A11yDialogOpen', { dialogKey: 'verify-password' });
            }
          }
        } else {
          this.validateAndSubmit();
        }
      },

      disableAccount() {
        Event.$emit('A11yDialogOpen', { dialogKey: 'confirm-disable-accountforCustomer' });
      },

      sendPasswordReset() {
        // No search values needed because a web agent is logged in
        const request = {
          username: this.editEmail,
          ldc: this.ldc,
          licenseKey: '123456',
          customerAccountId: null,
          phoneNumber: null,
        };
        this.loading = true;
        createAccount.sendPasswordResetTokenWebAgent(request)
          .then(() => {
            this.loading = false;
            this.passwordResetSuccess = true;
          })
          .catch(() => {
            this.passwordResetSuccess = false;
            this.loading = false;
          });
      },

      validate() {
        let hasError = false;
        // validate first name
        if (!this.editFirstName) {
          createError(this.$refs.editFirstName, this.$refs.editFirstNameError, 'Please provide a first name.');
          hasError = true
        } else {
          removeError(this.$refs.editFirstName, this.$refs.editFirstNameError);
        }

        // validate last
        if (!this.editLastName) {
          createError(this.$refs.editLastName, this.$refs.editLastNameError, 'Please provide a last name.');
          hasError = true
        } else {
          removeError(this.$refs.editLastName, this.$refs.editLastNameError);
        }

        // validate email
        if (!this.editEmail) {
          createError(this.$refs.editEmail, this.$refs.editEmailError, 'Please provide an email address.');
          hasError = true
        } else if (!regex.email.test(this.editEmail)) {
          createError(this.$refs.editEmail, this.$refs.editEmailError, this.errorMessages[this.$refs.editEmail.dataset.key].SecondaryMessage);
          hasError = true
        } else {
          removeError(this.$refs.editEmail, this.$refs.editEmailError);
        }

        // confirm emails are the same
        if (this.confirmEmail.toLowerCase() !== this.editEmail.toLowerCase()) {
          createError(this.$refs.confirmEmail, this.$refs.confirmEmailError, this.errorMessages[this.$refs.confirmEmail.dataset.key].SecondaryMessage);
          hasError = true
        } else {
          removeError(this.$refs.confirmEmail, this.$refs.confirmEmailError);
        }

        // validate password fields
        if (this.editPassword || this.confirmPassword) {
          if (!this.editPassword) {
            createError(this.$refs.editPassword, this.$refs.editPasswordError, this.errorMessages[this.$refs.editPassword.dataset.key].SecondaryMessage);
            hasError = true;
          } else if (!isPasswordValid(this.editPassword)) {
            createError(this.$refs.editPassword, this.$refs.editPasswordError, this.errorMessages[this.$refs.editPassword.dataset.key].SecondaryMessage);
            hasError = true
          } else {
            removeError(this.$refs.editPassword, this.$refs.editPasswordError);
          }

          // validate confirm password field
          if (!this.confirmPassword) {
            createError(this.$refs.confirmPassword, this.$refs.confirmPasswordError, 'Please confirm password');
            hasError = true
          } else if (this.editPassword !== this.confirmPassword) {
            createError(this.$refs.confirmPassword, this.$refs.confirmPasswordError, this.errorMessages[this.$refs.confirmPassword.dataset.key].SecondaryMessage);
            hasError = true
          } else {
            removeError(this.$refs.confirmPassword, this.$refs.confirmPasswordError);
          }
        } else {
          removeError(this.$refs.editPassword, this.$refs.editPasswordError);
        }
        return hasError;
      },

      checkCharLength() {
        if (this.editEmail.length > 50) {
          createError(this.$refs.editEmail, this.$refs.editEmailError, 'Email address cannot be more than 50 characters.');
        } else {
          removeError(this.$refs.editEmail, this.$refs.editEmailError);
        }
      },

      validateAndSubmit() {
        // when the user clicks on the save button, validate there's a value in the input field. if empty, show error
        // for email, validate the value in the email field is a valid email. if not valid, show error
        // make sure that confirmation fields have the same value as their partner
        // if there are no errors, let the user submit.
        const hasError = this.validate()

        if (hasError) {
          // update live text to let the user know there are errors
          this.liveText = 'The form has errors. Please make sure your profile information is correct.';
        } else {
          // save info
          // update live text to let the user know the info has saved
          this.liveText = 'Your web profile has been updated!';
          this.loading = true;
          this.emailValidationFailed = false;
          this.hidePasswordText();
          if (window.fabricator) {
            setTimeout(() => {
              this.loading = false;
              this.success = true;
              this.scrollUp();
            }, 500);
          } else {
            const profileRequest = {
              firstName: this.editFirstName,
              lastName: this.editLastName,
              businessName: this.editBusinessName,
              email: this.editEmail,
              password: this.editPassword,
              confirmPassword: this.confirmPassword,
              verifyPassword: this.verifyPassword,
              ldc: this.ldc,
              emailValidationFailedCount: this.emailValidationFailedCount,
              selectedAccount: getSelectedCustomerAccountId(),
            };
            profileApi.editProfile(profileRequest)
              .then(() => {
                this.loading = false;
                this.success = true;
                if (!window.fabricator) {
                  if (this.emailChanged) {
                    window.location.href = "/account/profile/success?usernameChanged=true";
                  } else {
                    window.location.href = "/account/profile/success";
                  }
                }
              })
              .catch((error) => {
                this.loading = false;
                if (error.response.status === 400 && error.response.data.error.code === 1060) {
                  removeErrors();
                  this.emailValidationFailed = true;
                  this.emailValidationFailedCount = this.emailValidationFailedCount + 1;
                  cookies.set(editProfileEmailValidationCountCookieName, this.emailValidationFailedCount);
                }
                this.scrollUp();
              });
          }
        }
      },
    },
  });
}



// WEBPACK FOOTER //
// ./src/assets/toolkit/scripts/portal/editProfile.js